/**
 * Estonian catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Estonian at a mathematician's register. **Warrant → «Põhjendus»**
 * (justification — flagged). Estonian counts take the partitive: «1 piirang» but
 * «2 piirangut». W/M grades stay Latin (`::numbering-system/latn`). CLDR cardinal categories
 * are one / other, so `frameActive` declares exactly those (plus an explicit `=0`). Per
 * INV-STABLE-ID a wrong translation is a presentation concern only — verdicts stay fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const ET_CATALOG = {
    locale: 'et',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Uurimine',
        [P2.sectionClaims]: 'Väited',
        [P2.sectionIntegrity]: 'Terviklikkus',
        [P2.axisWarrantLabel]: 'Põhjendus',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empiiriline',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definitsioon} interp {Tõlgendus} testable {Testitav} tested {Testitud} refuted {Ümber lükatud} program {Uurimisprogramm} philos {Filosoofiline} other {Tundmatu}}',
        [P2.axisModalLabel]: 'Modaalne',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Tugevuse tase {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Uuritav',
        [P2.stateRefuted]: 'Ümber lükatud',
        [P2.stateOutside]: 'Väljaspool ulatust',
        [P2.verdictConflict]: 'Vastuolus elemendiga {constraints}',
        [P2.verdictOutside]: 'Vajab {missing}',
        [P2.lossRegisterPlain]: 'Lihtsustatud tavakeelde — osa täpsusest jäetakse välja.',
        [P2.lossRegisterKid]: 'Lihtsustatud noorematele lugejatele — paljud üksikasjad jäetakse välja.',
        [P2.lossProfileDetail]: 'Täielik profiil Põhjendus / Empiiriline / Modaalne on siin kokku võetud.',
        [P2.lossConflictDetail]: 'Vastuolu üksikasjad on selles vaates lühendatud.',
        [P2.laneVerified]: 'Kinnitatud',
        [P2.laneUnverified]: 'Kinnitamata',
        [P2.laneConflict]: 'Vastuolu tõestatud',
        [P2.constraintLogic]: 'Loogika',
        [P2.constraintLightBends]: 'Valgus paindub gravitatsiooniväljas',
        [P2.constraintParallelPostulate]: 'Paralleelide postulaat',
        [P2.claimScalarGravity]: 'Skalaarne gravitatsiooniteooria',
        [P2.claimNonEuclidean]: 'Mitte-eukleidiline geomeetria',
        [P2.claimSubstrateSr]: 'Erirelatiivsusteooria substraaditõlgendus',
        [P2.edgeExtObservation]: 'Väline vaatlus',
        [P2.edgeResourceSample]: 'Jagatud arvutusressurss',
        [P2.edgeHeuristicSample]: 'Heuristiline juhend',
        [P2.edgeSharedSample]: 'Ühine alus',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Piirangud',
        [P2.sectionEdges]: 'Servad',
        [P2.sectionFrame]: 'Raam',
        [P2.sectionExposure]: 'Avatus',
        [P2.statusAdoptedFormal]: 'Vastu võetud (formaalne)',
        [P2.statusAdoptedInterpretive]: 'Vastu võetud (tõlgenduslik)',
        [P2.statusSuspended]: 'Peatatud',
        [P2.edgeEffectConstraint]: 'Moodustab piirangu (võib ümber lükata)',
        [P2.edgeEffectResource]: 'Ressurss — ei mõjuta otsuseid',
        [P2.edgeEffectHeuristic]: 'Heuristika — ei mõjuta otsuseid',
        [P2.edgeEffectShared]: 'Ühine alus — ei mõjuta otsuseid',
        [P2.frameActive]: '{count, plural, =0 {Aktiivseid piiranguid pole} one {# aktiivne piirang} other {# aktiivset piirangut}}',
        [P2.exposureNone]: 'Tõlgenduslikke sõltuvusi pole',
        [P2.exposureDep]: 'Sõltub elemendist {id}',
        [P2.laneTrackMachine]: 'Masin',
        [P2.laneTrackExpert]: 'Ekspert',
        [P2.laneTrackCommunity]: 'Kogukond',
        [P2.expertAudited]: 'Auditeeritud',
        [P2.expertInProgress]: 'Pooleli',
        [P2.expertNotReviewed]: 'Üle vaatamata',
        [P2.communityFlags]: 'Märgistused olemas',
        [P2.communityNone]: 'Märgistusi pole',
        [P2.controlChallenge]: 'Vaidlusta',
        [P2.controlActivate]: 'Aktiveeri',
        [P2.controlMakeInterpretive]: 'Muuda tõlgenduslikuks',
        [P2.controlImportEdge]: 'Impordi serv',
        [P2.controlPromote]: 'Edenda',
        [P2.controlUndo]: 'Võta tagasi',
        [P2.controlRedo]: 'Tee uuesti',
        [P2.controlRegister]: 'Registreeri',
        [P2.promoteRejected]: 'Edendamine tagasi lükatud',
        [P2.promoteAccepted]: 'Edendamine vastu võetud',
        [P2.promoteReasonRefutationTerminal]: 'Tõestus ei saa parandada ümberlükkamist.',
        [P2.promoteReasonKeystone]: 'Nurgakivi-edendamine vajab empiirilist tuge.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Empiiriline tüüp pole fikseeritud.',
        [P2.promoteReasonModalNotFixed]: 'Modaalne aste pole fikseeritud.',
        [P2.promoteReasonWarrantNotImproved]: 'Põhjenduse aste pole paranenud.',
        [P2.evidenceComputation]: "{engine} arvutas {op} = {result} tingimustes {config}",
        [P2.lossNumericRounding]: "Tulemus ümardatud {dp} kümnendkohani",
    },
};
//# sourceMappingURL=et.js.map